import type {
  CustomFieldBulkValuesResponse,
  CustomFieldEntityType,
  CustomFieldFilterOperator,
  CustomFieldFilterResponse,
  CustomFieldSchema,
  FilterCustomFieldsDto,
} from '@/types/custom-fields';
import {
  customFieldDraftToPayloadValue,
  formatCustomFieldDisplayValue,
} from './custom-field-form-utils';

export type CustomFieldColumnKey = `customField:${string}`;

export interface CustomFieldTableFilterState {
  fieldDefinitionId: string;
  operator: CustomFieldFilterOperator;
  value: string;
  valueTo: string;
}

const CUSTOM_FIELD_COLUMN_PREFIX = 'customField:';
const VALUELESS_OPERATORS: CustomFieldFilterOperator[] = ['IS_EMPTY', 'IS_NOT_EMPTY'];

export const EMPTY_CUSTOM_FIELD_TABLE_FILTER: CustomFieldTableFilterState = {
  fieldDefinitionId: '',
  operator: 'EQUALS',
  value: '',
  valueTo: '',
};

export function customFieldTableFilterNeedsValue(operator: CustomFieldFilterOperator) {
  return !VALUELESS_OPERATORS.includes(operator);
}

export function toCustomFieldColumnKey(fieldDefinitionId: string): CustomFieldColumnKey {
  return `${CUSTOM_FIELD_COLUMN_PREFIX}${fieldDefinitionId}` as CustomFieldColumnKey;
}

export function isCustomFieldColumnKey(value: string): value is CustomFieldColumnKey {
  return value.startsWith(CUSTOM_FIELD_COLUMN_PREFIX);
}

export function getCustomFieldIdFromColumnKey(value: string): string {
  return value.slice(CUSTOM_FIELD_COLUMN_PREFIX.length);
}

export function sortCustomFieldSchemas(schemas: CustomFieldSchema[]): CustomFieldSchema[] {
  return [...schemas].sort((left, right) => {
    const orderDiff = left.association.displayOrder - right.association.displayOrder;
    if (orderDiff !== 0) return orderDiff;
    return left.definition.label.localeCompare(right.definition.label);
  });
}

export function getCustomFieldValueForEntity(
  valuesByEntityId: CustomFieldBulkValuesResponse,
  entityId: string | undefined,
  fieldDefinitionId: string,
): unknown {
  if (!entityId) return null;
  return (
    valuesByEntityId[entityId]?.find((value) => value.fieldDefinitionId === fieldDefinitionId)
      ?.value ?? null
  );
}

export function formatCustomFieldTableValue(
  schemas: CustomFieldSchema[],
  valuesByEntityId: CustomFieldBulkValuesResponse,
  entityId: string | undefined,
  fieldDefinitionId: string,
): string {
  const schema = schemas.find((item) => item.definition.id === fieldDefinitionId);
  if (!schema) return '—';
  return formatCustomFieldDisplayValue(
    schema,
    getCustomFieldValueForEntity(valuesByEntityId, entityId, fieldDefinitionId),
  );
}

export function mergeColumnPrefs<T extends string>(
  current: { order: T[]; visible: T[] },
  allKeys: T[],
  defaultVisible: T[],
): { order: T[]; visible: T[] } {
  const validOrder = current.order.filter((key) => allKeys.includes(key));
  const missingKeys = allKeys.filter((key) => !validOrder.includes(key));
  const visible = current.visible.filter((key) => allKeys.includes(key));
  const next = {
    order: [...validOrder, ...missingKeys],
    visible: visible.length > 0 ? visible : defaultVisible,
  };

  if (
    next.order.length === current.order.length &&
    next.visible.length === current.visible.length &&
    next.order.every((key, index) => key === current.order[index]) &&
    next.visible.every((key, index) => key === current.visible[index])
  ) {
    return current;
  }

  return next;
}

function isValueReady(schema: CustomFieldSchema, filter: CustomFieldTableFilterState) {
  if (!customFieldTableFilterNeedsValue(filter.operator)) return true;
  if (schema.definition.fieldType === 'CHECKBOX') return true;
  if (filter.operator === 'BETWEEN') return filter.value !== '' && filter.valueTo !== '';
  return filter.value !== '';
}

export function buildCustomFieldTableFilterDto(
  entityType: CustomFieldEntityType,
  schemas: CustomFieldSchema[],
  filter: CustomFieldTableFilterState,
): FilterCustomFieldsDto | null {
  const schema = schemas.find((item) => item.definition.id === filter.fieldDefinitionId);
  if (!schema || !isValueReady(schema, filter)) return null;

  let value: unknown = undefined;
  if (customFieldTableFilterNeedsValue(filter.operator)) {
    if (filter.operator === 'BETWEEN') {
      value = [
        customFieldDraftToPayloadValue(schema, filter.value),
        customFieldDraftToPayloadValue(schema, filter.valueTo),
      ];
    } else {
      value = customFieldDraftToPayloadValue(schema, filter.value);
      if ((filter.operator === 'IN' || filter.operator === 'NOT_IN') && !Array.isArray(value)) {
        value = value === null || value === undefined ? [] : [value];
      }
    }
  }

  return {
    entityType,
    filters: [
      {
        fieldDefinitionId: schema.definition.id,
        operator: filter.operator,
        ...(customFieldTableFilterNeedsValue(filter.operator) ? { value } : {}),
      },
    ],
  };
}

interface CustomFieldServerFilterQueryStateInput {
  filter: FilterCustomFieldsDto | null;
  response: CustomFieldFilterResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Coordinates a custom-field ID lookup with the entity list query that consumes
 * its result. A selected field is not necessarily a runnable filter (for
 * example, a text filter with no value), and a failed lookup is terminal rather
 * than an indefinitely pending request.
 */
export function getCustomFieldServerFilterQueryState({
  filter,
  response,
  isLoading,
  isError,
}: CustomFieldServerFilterQueryStateInput) {
  const hasRunnableFilter = filter !== null;
  const hasResponse = hasRunnableFilter && response !== undefined;

  return {
    shouldApplyFilter: hasResponse,
    shouldEnableEntityQuery: !hasRunnableFilter || hasResponse,
    isResolvingFilter: hasRunnableFilter && isLoading,
    // React Query can retain a successful response when a background refetch
    // fails. Keep using those matching IDs and reserve the blocking error state
    // for initial lookups that have no usable response.
    isFilterError: hasRunnableFilter && !hasResponse && isError,
  };
}
